import type { LucideIcon } from 'lucide-react'

import { AtSign, Camera, Coins, Laptop, Send, Video, Wrench } from 'lucide-react'

/**
 * The marketplace content shown in the hero composition.
 *
 * The hero is a PREVIEW of the product, so every card is a real listing shape
 * rather than decoration: the same label / spec / price / seller-handle a
 * storefront card carries. Keeping it here rather than inside the components
 * is what makes the rotation possible — each floating card owns a POOL, and
 * one card at a time advances through its own pool so the composition reads as
 * a live marketplace instead of a still.
 *
 * TUNING. Add a listing by appending to a pool. The FIRST entry of each pool
 * is what the composition rests on before any rotation has happened, so that
 * is the one to change if you want a different card at first paint. Pools are
 * deliberately mixed across categories: the bottom-right card is dropped on
 * mobile, so no category may live in only one pool or a phone would never
 * see it.
 */

/** The five market categories, named as the brand layer names their plates. */
export type ListingTone = 'usernames' | 'crypto' | 'video' | 'accounts' | 'channels'

export type ListingFlag = 'new' | 'hot'

export interface HeroListing {
    id: string
    icon: LucideIcon
    tone: ListingTone
    /** What the listing IS — the small line above the spec. */
    label: string
    /** The spec a buyer scans for: a handle, a balance, a follower count. */
    detail: string
    price: string
    /** The seller. */
    handle: string
    flag?: ListingFlag
}

/**
 * Category plates, straight off the brand layer. They are wayfinding only and
 * sit BEHIND a near-black icon, never carrying text themselves. There are five
 * and there is no sixth: a new tone gets added to `globals.css` first.
 */
export const TONE_PLATE: Record<ListingTone, string> = {
    usernames: 'var(--cat-usernames)',
    crypto: 'var(--cat-crypto)',
    video: 'var(--cat-youtube)',
    accounts: 'var(--cat-accounts)',
    channels: 'var(--cat-influencers)'
}

/** The listing on the phone screen. It is the anchor, so it never rotates. */
export const HERO_PHONE_LISTING: HeroListing = {
    id: 'phone-tiktok',
    icon: Video,
    tone: 'video',
    label: 'TikTok account',
    detail: '246K followers',
    price: '$450',
    handle: '@digitalplug',
    flag: 'hot'
}

/**
 * One pool per floating card, in composition order: top-left, top-right,
 * bottom-left, bottom-right.
 */
export const HERO_LISTING_POOLS: ReadonlyArray<ReadonlyArray<HeroListing>> = [
    [
        {
            id: 'username-warrior',
            icon: AtSign,
            tone: 'usernames',
            label: 'Premium username',
            detail: '@warrior.albania',
            price: '$220',
            handle: '@name.market',
            flag: 'new'
        },
        {
            id: 'channel-kanali',
            icon: Send,
            tone: 'channels',
            label: 'Telegram channel',
            detail: '18K members',
            price: '$640',
            handle: '@kanali.ks',
            flag: 'hot'
        },
        {
            id: 'service-repair',
            icon: Wrench,
            tone: 'crypto',
            label: 'Local service',
            detail: 'Phone repair, Tirana',
            price: '$25',
            handle: '@fix.tirana'
        }
    ],
    [
        {
            id: 'video-nextcreators',
            icon: Video,
            tone: 'video',
            label: 'YouTube channel',
            detail: '234K subs',
            price: '$1,250',
            handle: '@next.creators',
            flag: 'hot'
        },
        {
            id: 'electronics-iphone',
            icon: Laptop,
            tone: 'accounts',
            label: 'Electronics',
            detail: 'iPhone 15 Pro, 256GB',
            price: '$780',
            handle: '@tek.store',
            flag: 'new'
        },
        {
            id: 'username-ksdeals',
            icon: AtSign,
            tone: 'usernames',
            label: 'Premium username',
            detail: '@ks.deals',
            price: '$310',
            handle: '@name.market'
        }
    ],
    [
        {
            id: 'crypto-usdt',
            icon: Coins,
            tone: 'crypto',
            label: 'Crypto deal',
            detail: 'USDT balance',
            price: '$2,500',
            handle: '@crypto.mta'
        },
        {
            id: 'crypto-p2p',
            icon: Coins,
            tone: 'crypto',
            label: 'Crypto P2P',
            detail: 'BTC for cash',
            price: '$1,900',
            handle: '@p2p.albania',
            flag: 'hot'
        },
        {
            id: 'service-delivery',
            icon: Wrench,
            tone: 'channels',
            label: 'Local service',
            detail: 'Delivery, Prishtina',
            price: '$18',
            handle: '@nisu.ks'
        }
    ],
    [
        {
            id: 'social-instaalb',
            icon: Camera,
            tone: 'accounts',
            label: 'Instagram account',
            detail: '58K followers',
            price: '$180',
            handle: '@insta.alb',
            flag: 'new'
        },
        {
            id: 'social-viralks',
            icon: Video,
            tone: 'video',
            label: 'TikTok account',
            detail: '92K followers',
            price: '$540',
            handle: '@viral.ks',
            flag: 'hot'
        },
        {
            id: 'electronics-macbook',
            icon: Laptop,
            tone: 'accounts',
            label: 'Electronics',
            detail: 'MacBook Air M2',
            price: '$960',
            handle: '@tek.store'
        }
    ]
]
